import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, formatApiError, reportingWeek, formatWeek, formatMinutes, lastNMondays } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import { Home, Clock, MapPin, Handshake, FileSignature, Users, TrendingUp, ArrowRight, CheckCircle2, AlertCircle, Loader2, CalendarDays } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(reportingWeek());

  useEffect(() => {
    (async () => {
      try {
        const [ov, us, rp] = await Promise.all([
          http.get("/stats/overview"),
          http.get("/users"),
          http.get("/reports"),
        ]);
        setOverview(ov.data);
        setUsers(us.data);
        setAllReports(rp.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const collaborators = useMemo(() => users.filter((u) => u.role === "collaborator"), [users]);
  const currentWeekReports = useMemo(
    () => allReports.filter((r) => r.week_start === currentWeek),
    [allReports, currentWeek]
  );
  const submittedUserIds = useMemo(
    () => new Set(currentWeekReports.map((r) => r.user_id)),
    [currentWeekReports]
  );
  const latestByUser = useMemo(() => {
    const m = new Map();
    for (const r of allReports) {
      const prev = m.get(r.user_id);
      if (!prev || r.week_start > prev) m.set(r.user_id, r.week_start);
    }
    return m;
  }, [allReports]);

  const chartData = useMemo(() => {
    if (!overview) return [];
    return [...overview.per_user]
      .sort((a, b) => (b.visites + b.relances_vendeurs + b.prospection_minutes / 30) - (a.visites + a.relances_vendeurs + a.prospection_minutes / 30))
      .map((u) => ({
        name: u.name.split(" ")[0],
        Visites: u.visites,
        Relances: u.relances_vendeurs,
        "Prospection (h)": Math.round(u.prospection_minutes / 6) / 10,
      }));
  }, [overview]);

  if (loading || !overview) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-testid="admin-dashboard">
        <div>
          <p className="label-eyebrow mb-2">Vue d'ensemble — semaine du {formatWeek(currentWeek)}</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Tableau de bord</h1>
          <p className="mt-2 text-slate-600">Suivi consolidé de l'activité de l'équipe.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={currentWeek} onValueChange={setCurrentWeek}>
            <SelectTrigger className="sm:w-[240px] gap-2" data-testid="admin-week-selector">
              <CalendarDays className="h-4 w-4 text-primary" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {lastNMondays(10).map((w) => (
                <SelectItem key={w} value={w}>Semaine du {formatWeek(w)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => navigate("/admin/collaborateurs")} data-testid="add-collab-shortcut">
            <Users className="h-4 w-4 mr-2" />
            Collaborateurs
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <BigStat icon={Users} label="Collaborateurs" value={overview.collaborators_count} />
        <BigStat icon={Home} label="Visites cumulées" value={overview.totals.visites} />
        <BigStat icon={Clock} label="Prospection" value={formatMinutes(overview.totals.prospection_minutes)} />
        <BigStat icon={MapPin} label="Relances" value={overview.totals.relances_vendeurs} />
        <BigStat icon={Handshake} label="Compromis en cours" value={overview.totals.compromis_en_cours} />
      </div>

      {/* Weekly submission status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="p-6 lg:col-span-2" data-testid="week-status-card">
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="label-eyebrow mb-1">État de la semaine en cours</p>
              <h2 className="font-display font-bold text-2xl">Qui a rempli son rapport ?</h2>
            </div>
            <Badge variant={currentWeekReports.length === collaborators.length && collaborators.length > 0 ? "default" : "outline"} className="text-sm px-3 py-1">
              {currentWeekReports.length}/{collaborators.length} transmis
            </Badge>
          </div>
          {collaborators.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              Aucun collaborateur pour le moment. <Button variant="link" onClick={() => navigate("/admin/collaborateurs")}>Ajouter</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {collaborators.map((u) => {
                const done = submittedUserIds.has(u.id);
                const lastWeek = latestByUser.get(u.id);
                const hasAny = !!lastWeek;
                let label, tone, Icon;
                if (done) {
                  label = "Rapport transmis";
                  tone = "emerald";
                  Icon = CheckCircle2;
                } else if (hasAny && lastWeek > currentWeek) {
                  label = `A rempli une semaine plus récente : ${formatWeek(lastWeek)}`;
                  tone = "sky";
                  Icon = CheckCircle2;
                } else if (hasAny) {
                  label = `En attente — dernier rapport : ${formatWeek(lastWeek)}`;
                  tone = "amber";
                  Icon = AlertCircle;
                } else {
                  label = "Aucun rapport transmis à ce jour";
                  tone = "red";
                  Icon = AlertCircle;
                }
                const tones = {
                  emerald: { bg: "bg-emerald-50 border-emerald-200", icon: "text-emerald-600", text: "text-emerald-700" },
                  sky: { bg: "bg-sky-50 border-sky-200", icon: "text-sky-600", text: "text-sky-700" },
                  amber: { bg: "bg-amber-50 border-amber-200", icon: "text-amber-600", text: "text-amber-700" },
                  red: { bg: "bg-red-50 border-red-200", icon: "text-red-600", text: "text-red-700" },
                };
                const t = tones[tone];
                return (
                  <div
                    key={u.id}
                    className={`flex items-center justify-between p-3 rounded-md border ${t.bg}`}
                    data-testid={`week-status-${u.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`h-5 w-5 ${t.icon}`} />
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </div>
                    </div>
                    <div className={`text-xs font-medium text-right ${t.text}`}>{label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-6" data-testid="quick-shortcuts">
          <p className="label-eyebrow mb-1">Réunion de lundi</p>
          <h2 className="font-display font-bold text-2xl mb-4">Actions rapides</h2>
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/admin/rapports")}>
              Voir tous les rapports <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/admin/stats")}>
              Comparer les négociateurs <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/admin/collaborateurs")}>
              Ajouter un collaborateur <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>

      {/* Comparison chart */}
      <Card className="p-6 mb-8" data-testid="agent-comparison-chart">
        <div className="mb-5">
          <p className="label-eyebrow mb-1">Comparaison</p>
          <h2 className="font-display font-bold text-2xl">Différence de travail par négociateur</h2>
          <p className="text-sm text-slate-500 mt-1">Cumul depuis le début. Utile pour situer l'effort de chacun.</p>
        </div>
        {chartData.length === 0 ? (
          <div className="text-center text-slate-500 py-10">Pas encore de données à comparer.</div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barCategoryGap={24}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 12 }} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
                <YAxis tick={{ fill: "#475569", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
                  cursor={{ fill: "rgba(29, 78, 216, 0.06)" }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Visites" fill="#1D4ED8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Relances" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Prospection (h)" fill="#16A34A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Weekly trend */}
      {overview.weekly_series.length > 0 && (
        <Card className="p-6" data-testid="weekly-trend-chart">
          <div className="mb-5">
            <p className="label-eyebrow mb-1">Tendance équipe</p>
            <h2 className="font-display font-bold text-2xl">Activité par semaine</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={overview.weekly_series}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="week_start" tick={{ fill: "#475569", fontSize: 11 }} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
                <YAxis tick={{ fill: "#475569", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="visites" name="Visites" stroke="#1D4ED8" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="relances_vendeurs" name="Relances" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </Layout>
  );
}

function BigStat({ icon: Icon, label, value }) {
  return (
    <Card className="p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid={`bigstat-${label}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
          <div className="font-display font-bold text-2xl text-slate-900 truncate">{value}</div>
        </div>
      </div>
    </Card>
  );
}
